import React, { useState, useEffect, useCallback } from 'react';
import api from './api';
import Spinner from './Spinner';
import CustomAlert from './CustomAlert';
import ExcelJS from 'exceljs';
import partTypeMap from './partTypeMap.json';

// Normalize a part number for matching against the checklist map
// (strips all whitespace and upper-cases, matching how the map was generated)
const normalizePartNo = (p) => (p == null ? '' : String(p).replace(/\s+/g, '').toUpperCase());

// Returns 'Consumable', 'Repairable', or '' if unknown
const lookupPartType = (partNo) => partTypeMap[normalizePartNo(partNo)] || '';

const getStyles = (theme) => {
    const isDark = theme === 'dark';
    const colors = {
        bg: isDark ? '#121212' : '#f4f7f6',
        cardBg: isDark ? '#1e1e1e' : '#fff',
        text: isDark ? '#e0e0e0' : '#333',
        subtleText: isDark ? '#a0a0a0' : '#666',
        border: isDark ? '#444' : '#ddd',
        shadow: isDark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)',
        link: isDark ? '#6cb2f7' : '#007bff',
    };

    return {
        container: { 
            backgroundColor: colors.cardBg, 
            borderRadius: '8px', 
            overflow: 'hidden', 
            boxShadow: `0 2px 5px ${colors.shadow}`,
            marginTop: '20px'
        },
        header: {
            backgroundColor: isDark ? '#2a2a2a' : '#f8f9fa',
            padding: '15px 20px',
            borderBottom: `2px solid ${colors.border}`,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
        },
        title: { 
            fontSize: '18px', 
            fontWeight: 'bold', 
            color: colors.text, 
            margin: 0 
        },
        controlsContainer: { 
            display: 'flex', 
            gap: '15px', 
            marginBottom: '20px', 
            flexWrap: 'wrap', 
            alignItems: 'center',
            padding: '15px 20px',
            borderBottom: `1px solid ${colors.border}`
        },
        searchInput: { 
            padding: '8px 12px', 
            borderRadius: '4px', 
            border: `1px solid ${colors.border}`, 
            backgroundColor: isDark ? '#333' : '#fff', 
            color: colors.text, 
            minWidth: '200px' 
        },
        filterSelect: { 
            padding: '8px 12px', 
            borderRadius: '4px', 
            border: `1px solid ${colors.border}`, 
            backgroundColor: isDark ? '#333' : '#fff', 
            color: colors.text 
        },
        filterHint: {
            fontSize: '12px',
            color: colors.subtleText,
            fontStyle: 'italic',
            display: 'inline-flex',
            alignItems: 'center',
            gap: '8px'
        },
        clearFilterButton: {
            padding: '4px 10px',
            backgroundColor: 'transparent',
            color: colors.link,
            border: `1px solid ${colors.link}`,
            borderRadius: '4px',
            cursor: 'pointer',
            fontSize: '11px',
            fontStyle: 'normal'
        },
        table: { 
            width: '100%', 
            borderCollapse: 'collapse' 
        },
        tableHeader: { 
            backgroundColor: isDark ? '#2a2a2a' : '#f8f9fa', 
            padding: '15px 10px', 
            borderBottom: `2px solid ${colors.border}` 
        },
        tableHeaderCell: { 
            padding: '12px 8px', 
            textAlign: 'left', 
            fontWeight: 'bold', 
            color: colors.text, 
            cursor: 'pointer', 
            userSelect: 'none', 
            position: 'relative',
            fontSize: '12px',
            whiteSpace: 'nowrap'
        },
        sortableHeader: { 
            display: 'flex', 
            alignItems: 'center', 
            gap: '5px' 
        },
        sortIcon: { 
            fontSize: '10px', 
            opacity: 0.7 
        },
        tableRow: { 
            borderBottom: `1px solid ${colors.border}`, 
            '&:hover': { backgroundColor: isDark ? '#2a2a2a' : '#f8f9fa' } 
        },
        tableCell: { 
            padding: '8px', 
            color: colors.text, 
            verticalAlign: 'top',
            fontSize: '11px',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            maxWidth: '120px'
        },
        siteBadge: {
            padding: '2px 6px',
            borderRadius: '4px',
            fontSize: '10px',
            fontWeight: 'bold',
            backgroundColor: colors.link,
            color: 'white'
        },
        partTypeBadge: {
            padding: '2px 6px',
            borderRadius: '4px',
            fontSize: '10px',
            fontWeight: 'bold',
            color: 'white',
            display: 'inline-block',
            minWidth: '70px',
            textAlign: 'center'
        },
        partTypeConsumable: {
            backgroundColor: '#28a745'
        },
        partTypeRepairable: {
            backgroundColor: '#fd7e14'
        },
        partTypeUnknown: {
            backgroundColor: isDark ? '#555' : '#adb5bd'
        },
        noData: {
            textAlign: 'center',
            padding: '40px',
            color: colors.subtleText,
            fontStyle: 'italic'
        },
        uploadButton: {
            padding: '8px 16px',
            backgroundColor: '#dc3545',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer',
            fontSize: '12px'
        },
        exportButton: {
            padding: '8px 16px',
            backgroundColor: '#28a745',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer',
            fontSize: '12px',
            marginLeft: '10px'
        },
        paginationContainer: {
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '15px 20px',
            borderTop: `1px solid ${colors.border}`,
            backgroundColor: isDark ? '#2a2a2a' : '#f8f9fa'
        },
        paginationInfo: {
            color: colors.subtleText,
            fontSize: '14px'
        },
        paginationControls: {
            display: 'flex',
            gap: '10px',
            alignItems: 'center'
        },
        paginationButton: {
            padding: '6px 12px',
            backgroundColor: colors.cardBg,
            color: colors.text,
            border: `1px solid ${colors.border}`,
            borderRadius: '4px',
            cursor: 'pointer',
            fontSize: '12px',
            minWidth: '40px'
        },
        paginationButtonDisabled: {
            padding: '6px 12px',
            backgroundColor: isDark ? '#333' : '#f5f5f5',
            color: colors.subtleText,
            border: `1px solid ${colors.border}`,
            borderRadius: '4px',
            cursor: 'not-allowed',
            fontSize: '12px',
            minWidth: '40px'
        },
        pageNumbers: {
            display: 'flex',
            gap: '5px',
            alignItems: 'center'
        },
        pageNumber: {
            padding: '6px 10px',
            backgroundColor: colors.cardBg,
            color: colors.text,
            border: `1px solid ${colors.border}`,
            borderRadius: '4px',
            cursor: 'pointer',
            fontSize: '12px',
            minWidth: '30px',
            textAlign: 'center'
        },
        currentPage: {
            padding: '6px 10px',
            backgroundColor: colors.link,
            color: 'white',
            border: `1px solid ${colors.link}`,
            borderRadius: '4px',
            fontSize: '12px',
            minWidth: '30px',
            textAlign: 'center'
        }
    };
};

function NMCSPMCSTable({ theme, sites, onUpload }) {
    const styles = getStyles(theme);
    const [nmcsPmcsData, setNmcsPmcsData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [siteFilter, setSiteFilter] = useState('');
    const [partTypeFilter, setPartTypeFilter] = useState(''); // '', 'Consumable', 'Repairable', 'Unknown'
    const [sortField, setSortField] = useState('');
    const [sortDirection, setSortDirection] = useState('asc');
    const [alert, setAlert] = useState({ show: false, message: '', type: '' });
    
    // Pagination state
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [totalCount, setTotalCount] = useState(0);
    const [pageSize] = useState(100);

    // Server handles search/site filtering + pagination.
    // Part Type filter is applied client-side against the current page
    // because the API doesn't know about checklist matches.
    const displayData = partTypeFilter
        ? nmcsPmcsData.filter(row => {
            const pt = lookupPartType(row.part_no) || 'Unknown';
            return pt === partTypeFilter;
        })
        : nmcsPmcsData;

    const handleSort = (field) => {
        if (sortField === field) {
            setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
        } else {
            setSortField(field);
            setSortDirection('asc');
        }
    };

    const fetchNmcsPmcsData = useCallback((page = 1) => {
        setLoading(true);
        const params = new URLSearchParams();
        params.append('page', page);
        params.append('page_size', pageSize);
        
        // Add search and filter parameters if they exist
        if (searchTerm) params.append('search', searchTerm);
        if (siteFilter) params.append('site_name', siteFilter);
        
        api.get(`/nmcs-pmcs/?${params.toString()}`)
            .then(res => {
                const data = Array.isArray(res.data) ? res.data : (res.data.results || []);
                setNmcsPmcsData(data);
                
                // Handle pagination metadata
                if (res.data.count !== undefined) {
                    setTotalCount(res.data.count);
                    setTotalPages(Math.ceil(res.data.count / pageSize));
                } else {
                    setTotalCount(data.length);
                    setTotalPages(1);
                }
                setCurrentPage(page);
            })
            .catch(err => {
                console.error('Error fetching NMCS PMCS data:', err);
                setAlert({ show: true, message: 'Failed to fetch NMCS PMCS data.', type: 'error' });
            })
            .finally(() => setLoading(false));
    }, [pageSize, searchTerm, siteFilter]);

    useEffect(() => {
        fetchNmcsPmcsData(1);
    }, [fetchNmcsPmcsData]);

    // Pagination functions
    const handlePageChange = (page) => {
        if (page >= 1 && page <= totalPages) {
            fetchNmcsPmcsData(page);
        }
    };

    const handlePreviousPage = () => {
        if (currentPage > 1) {
            handlePageChange(currentPage - 1);
        }
    };

    const handleNextPage = () => {
        if (currentPage < totalPages) {
            handlePageChange(currentPage + 1);
        }
    };

    // Generate page numbers to display
    const getPageNumbers = () => {
        const pages = [];
        const maxVisiblePages = 5;
        let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
        let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);
        
        if (endPage - startPage + 1 < maxVisiblePages) {
            startPage = Math.max(1, endPage - maxVisiblePages + 1);
        }
        
        for (let i = startPage; i <= endPage; i++) {
            pages.push(i);
        }
        return pages;
    };

    const handleUploadClick = () => {
        onUpload('NMCS PMCS', 'Provides Estimated Delivery Date (EDDs) for when a part is going to be delivered for an aircraft');
    };

    const handleExportToExcel = async () => {
        try {
            setLoading(true);
            setAlert({ show: true, message: 'Generating Excel file...', type: 'info' });
            
            // Fetch all NMCS PMCS data without pagination or filters
            const response = await api.get('/nmcs-pmcs/');
            const allData = Array.isArray(response.data) ? response.data : (response.data.results || []);
            
            if (allData.length === 0) {
                setAlert({ show: true, message: 'No data available to export.', type: 'warning' });
                setLoading(false);
                return;
            }
            
            // Group data by site_name
            const dataBySite = {};
            allData.forEach(item => {
                const siteName = item.site_name || 'Unknown';
                if (!dataBySite[siteName]) {
                    dataBySite[siteName] = [];
                }
                dataBySite[siteName].push(item);
            });
            
            // Create Excel workbook
            const workbook = new ExcelJS.Workbook();
            
            // Define column headers (standard report layout + Part Type)
            const headers = [
                'Site',
                'Modex',
                'BUNO',
                'JCN',
                'MCN',
                'EOC RCVD',
                'TI MCN',
                'WC CD',
                'WUC',
                'RQN DT',
                'Compute 0004',
                'RQN NO',
                'Proj',
                'Pri',
                'COG CD',
                'CAGE',
                'FSC',
                'NIIN',
                'Part No',
                'Part Type',
                'Nomenclature',
                'Qty Ord',
                'Ord DT',
                'Rcvd DT',
                'Est Delv DT',
                'UI',
                'Sup STS'
            ];
            
            // Create a worksheet for each site
            const siteNames = Object.keys(dataBySite).sort();
            
            for (const siteName of siteNames) {
                const siteData = dataBySite[siteName];
                if (!siteData || siteData.length === 0) continue;
                
                // Create worksheet for this site (Excel sheet names are limited to 31 characters)
                const sheetName = siteName.length > 31 ? siteName.substring(0, 31) : siteName;
                const worksheet = workbook.addWorksheet(sheetName);
                
                // Add header row
                const headerRow = worksheet.addRow(headers);
                headerRow.font = { bold: true, size: 11 };
                headerRow.fill = {
                    type: 'pattern',
                    pattern: 'solid',
                    fgColor: { argb: 'FFD3D3D3' }
                };
                headerRow.alignment = { horizontal: 'center', vertical: 'middle' };
                
                // Add data rows
                siteData.forEach((row, index) => {
                    const rowData = [
                        row.site_name || '',
                        row.modex || '',
                        row.serno || '',
                        row.jcn || '',
                        row.mcn || '',
                        row.eoc_rcvd || '',
                        row.ti_mcn || '',
                        row.wc_cd || '',
                        row.wuc || '',
                        row.rqn_dt || '',
                        row.compute_0004 || '',
                        row.rqn_no || '',
                        row.proj || '',
                        row.pri || '',
                        row.cog_cd || '',
                        row.cage || '',
                        row.fsc || '',
                        row.niin || '',
                        row.part_no || '',
                        lookupPartType(row.part_no) || 'Unknown',
                        row.nomen || '',
                        row.qty_ord || '',
                        row.ord_dt || '',
                        row.rcvd_dt || '',
                        row.est_delv_dt || '',
                        row.ui || '',
                        row.sup_sts || ''
                    ];
                    
                    const excelRow = worksheet.addRow(rowData);
                    
                    // Apply color banding (alternating row colors)
                    const isEvenRow = index % 2 === 0;
                    if (isEvenRow) {
                        const bandingColor = 'FFF2F2F2'; // Light gray
                        excelRow.eachCell((cell) => {
                            cell.fill = {
                                type: 'pattern',
                                pattern: 'solid',
                                fgColor: { argb: bandingColor }
                            };
                        });
                    }

                    // Color-code the Part Type cell (column 20)
                    const partTypeValue = lookupPartType(row.part_no);
                    const partTypeCell = excelRow.getCell(20);
                    if (partTypeValue === 'Consumable') {
                        partTypeCell.fill = {
                            type: 'pattern',
                            pattern: 'solid',
                            fgColor: { argb: 'FF28A745' }
                        };
                        partTypeCell.font = { color: { argb: 'FFFFFFFF' }, bold: true };
                    } else if (partTypeValue === 'Repairable') {
                        partTypeCell.fill = {
                            type: 'pattern',
                            pattern: 'solid',
                            fgColor: { argb: 'FFFD7E14' }
                        };
                        partTypeCell.font = { color: { argb: 'FFFFFFFF' }, bold: true };
                    } else {
                        partTypeCell.font = { italic: true, color: { argb: 'FF6C757D' } };
                    }
                });
                
                // Set column widths
                worksheet.columns = [
                    { width: 12 }, // Site
                    { width: 10 }, // Modex
                    { width: 10 }, // BUNO
                    { width: 12 }, // JCN
                    { width: 12 }, // MCN
                    { width: 12 }, // EOC RCVD
                    { width: 12 }, // TI MCN
                    { width: 10 }, // WC CD
                    { width: 10 }, // WUC
                    { width: 12 }, // RQN DT
                    { width: 15 }, // Compute 0004
                    { width: 12 }, // RQN NO
                    { width: 8 },  // Proj
                    { width: 8 },  // Pri
                    { width: 10 }, // COG CD
                    { width: 12 }, // CAGE
                    { width: 8 },  // FSC
                    { width: 12 }, // NIIN
                    { width: 15 }, // Part No
                    { width: 12 }, // Part Type
                    { width: 40 }, // Nomenclature
                    { width: 10 }, // Qty Ord
                    { width: 12 }, // Ord DT
                    { width: 12 }, // Rcvd DT
                    { width: 12 }, // Est Delv DT
                    { width: 8 },  // UI
                    { width: 10 }  // Sup STS
                ];
                
                // Apply borders to all cells
                worksheet.eachRow((row) => {
                    row.eachCell((cell) => {
                        cell.border = {
                            top: { style: 'thin' },
                            left: { style: 'thin' },
                            bottom: { style: 'thin' },
                            right: { style: 'thin' }
                        };
                    });
                });
            }
            
            // Generate Excel file
            const buffer = await workbook.xlsx.writeBuffer();
            const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = `NMCS_PMCS_Export_${new Date().toISOString().split('T')[0]}.xlsx`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
            
            setAlert({ show: true, message: 'Excel file exported successfully!', type: 'success' });
        } catch (error) {
            console.error('Error exporting to Excel:', error);
            setAlert({ show: true, message: 'Failed to export Excel file.', type: 'error' });
        } finally {
            setLoading(false);
        }
    };

    return (
        <div style={styles.container}>
            <CustomAlert {...alert} onClose={() => setAlert({ show: false })} theme={theme} />
            
            <div style={styles.header}>
                <h2 style={styles.title}>NMCS PMCS Data</h2>
                <div>
                    <button onClick={handleExportToExcel} style={styles.exportButton}>
                        Export to Excel
                    </button>
                    <button onClick={handleUploadClick} style={styles.uploadButton}>
                        Upload New Data
                    </button>
                </div>
            </div>

            <div style={styles.controlsContainer}>
                <input
                    type="text"
                    placeholder="Search by Modex, BUNO, JCN, MCN, Part No, NIIN, WUC, or Nomenclature..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    style={styles.searchInput}
                />
                <select
                    value={siteFilter}
                    onChange={(e) => setSiteFilter(e.target.value)}
                    style={styles.filterSelect}
                >
                    <option value="">All Sites</option>
                    {Array.isArray(sites) && sites.map(site => (
                        <option key={site.id} value={site.name}>{site.name}</option>
                    ))}
                </select>
                <select
                    value={partTypeFilter}
                    onChange={(e) => setPartTypeFilter(e.target.value)}
                    style={styles.filterSelect}
                    title="Filter by checklist match (applies to the current page)"
                >
                    <option value="">All Part Types</option>
                    <option value="Consumable">Consumable</option>
                    <option value="Repairable">Repairable</option>
                    <option value="Unknown">Unknown</option>
                </select>
                {partTypeFilter && (
                    <span style={styles.filterHint}>
                        Showing {displayData.length} of {nmcsPmcsData.length} rows on this page
                        <button
                            onClick={() => setPartTypeFilter('')}
                            style={styles.clearFilterButton}
                            title="Clear Part Type filter"
                        >
                            Clear
                        </button>
                    </span>
                )}
            </div>

            {loading ? (
                <div style={{ padding: '40px', textAlign: 'center' }}>
                    <Spinner />
                </div>
            ) : (
                <div style={{ overflowX: 'auto' }}>
                    <table style={styles.table}>
                        <thead>
                            <tr style={styles.tableHeader}>
                                <th style={styles.tableHeaderCell}>
                                    <div style={styles.sortableHeader} onClick={() => handleSort('site_name')}>
                                        Site
                                        {sortField === 'site_name' && (
                                            <span style={styles.sortIcon}>
                                                {sortDirection === 'asc' ? '↑' : '↓'}
                                            </span>
                                        )}
                                    </div>
                                </th>
                                <th style={styles.tableHeaderCell}>
                                    <div style={styles.sortableHeader} onClick={() => handleSort('modex')}>
                                        Modex
                                        {sortField === 'modex' && (
                                            <span style={styles.sortIcon}>
                                                {sortDirection === 'asc' ? '↑' : '↓'}
                                            </span>
                                        )}
                                    </div>
                                </th>
                                <th style={styles.tableHeaderCell}>
                                    <div style={styles.sortableHeader} onClick={() => handleSort('serno')}>
                                        BUNO
                                        {sortField === 'serno' && (
                                            <span style={styles.sortIcon}>
                                                {sortDirection === 'asc' ? '↑' : '↓'}
                                            </span>
                                        )}
                                    </div>
                                </th>
                                <th style={styles.tableHeaderCell}>
                                    <div style={styles.sortableHeader} onClick={() => handleSort('jcn')}>
                                        JCN
                                        {sortField === 'jcn' && (
                                            <span style={styles.sortIcon}>
                                                {sortDirection === 'asc' ? '↑' : '↓'}
                                            </span>
                                        )}
                                    </div>
                                </th>
                                <th style={styles.tableHeaderCell}>
                                    <div style={styles.sortableHeader} onClick={() => handleSort('mcn')}>
                                        MCN
                                        {sortField === 'mcn' && (
                                            <span style={styles.sortIcon}>
                                                {sortDirection === 'asc' ? '↑' : '↓'}
                                            </span>
                                        )}
                                    </div>
                                </th>
                                <th style={styles.tableHeaderCell}>
                                    <div style={styles.sortableHeader} onClick={() => handleSort('eoc_rcvd')}>
                                        EOC RCVD
                                        {sortField === 'eoc_rcvd' && (
                                            <span style={styles.sortIcon}>
                                                {sortDirection === 'asc' ? '↑' : '↓'}
                                            </span>
                                        )}
                                    </div>
                                </th>
                                <th style={styles.tableHeaderCell}>
                                    <div style={styles.sortableHeader} onClick={() => handleSort('ti_mcn')}>
                                        TI MCN
                                        {sortField === 'ti_mcn' && (
                                            <span style={styles.sortIcon}>
                                                {sortDirection === 'asc' ? '↑' : '↓'}
                                            </span>
                                        )}
                                    </div>
                                </th>
                                <th style={styles.tableHeaderCell}>
                                    <div style={styles.sortableHeader} onClick={() => handleSort('wc_cd')}>
                                        WC CD
                                        {sortField === 'wc_cd' && (
                                            <span style={styles.sortIcon}>
                                                {sortDirection === 'asc' ? '↑' : '↓'}
                                            </span>
                                        )}
                                    </div>
                                </th>
                                <th style={styles.tableHeaderCell}>
                                    <div style={styles.sortableHeader} onClick={() => handleSort('wuc')}>
                                        WUC
                                        {sortField === 'wuc' && (
                                            <span style={styles.sortIcon}>
                                                {sortDirection === 'asc' ? '↑' : '↓'}
                                            </span>
                                        )}
                                    </div>
                                </th>
                                <th style={styles.tableHeaderCell}>
                                    <div style={styles.sortableHeader} onClick={() => handleSort('rqn_dt')}>
                                        RQN DT
                                        {sortField === 'rqn_dt' && (
                                            <span style={styles.sortIcon}>
                                                {sortDirection === 'asc' ? '↑' : '↓'}
                                            </span>
                                        )}
                                    </div>
                                </th>
                                <th style={styles.tableHeaderCell}>
                                    <div style={styles.sortableHeader} onClick={() => handleSort('compute_0004')}>
                                        Compute 0004
                                        {sortField === 'compute_0004' && (
                                            <span style={styles.sortIcon}>
                                                {sortDirection === 'asc' ? '↑' : '↓'}
                                            </span>
                                        )}
                                    </div>
                                </th>
                                <th style={styles.tableHeaderCell}>
                                    <div style={styles.sortableHeader} onClick={() => handleSort('rqn_no')}>
                                        RQN NO
                                        {sortField === 'rqn_no' && (
                                            <span style={styles.sortIcon}>
                                                {sortDirection === 'asc' ? '↑' : '↓'}
                                            </span>
                                        )}
                                    </div>
                                </th>
                                <th style={styles.tableHeaderCell}>
                                    <div style={styles.sortableHeader} onClick={() => handleSort('proj')}>
                                        Proj
                                        {sortField === 'proj' && (
                                            <span style={styles.sortIcon}>
                                                {sortDirection === 'asc' ? '↑' : '↓'}
                                            </span>
                                        )}
                                    </div>
                                </th>
                                <th style={styles.tableHeaderCell}>
                                    <div style={styles.sortableHeader} onClick={() => handleSort('pri')}>
                                        Pri
                                        {sortField === 'pri' && (
                                            <span style={styles.sortIcon}>
                                                {sortDirection === 'asc' ? '↑' : '↓'}
                                            </span>
                                        )}
                                    </div>
                                </th>
                                <th style={styles.tableHeaderCell}>
                                    <div style={styles.sortableHeader} onClick={() => handleSort('cog_cd')}>
                                        COG CD
                                        {sortField === 'cog_cd' && (
                                            <span style={styles.sortIcon}>
                                                {sortDirection === 'asc' ? '↑' : '↓'}
                                            </span>
                                        )}
                                    </div>
                                </th>
                                <th style={styles.tableHeaderCell}>
                                    <div style={styles.sortableHeader} onClick={() => handleSort('cage')}>
                                        CAGE
                                        {sortField === 'cage' && (
                                            <span style={styles.sortIcon}>
                                                {sortDirection === 'asc' ? '↑' : '↓'}
                                            </span>
                                        )}
                                    </div>
                                </th>
                                <th style={styles.tableHeaderCell}>
                                    <div style={styles.sortableHeader} onClick={() => handleSort('fsc')}>
                                        FSC
                                        {sortField === 'fsc' && (
                                            <span style={styles.sortIcon}>
                                                {sortDirection === 'asc' ? '↑' : '↓'}
                                            </span>
                                        )}
                                    </div>
                                </th>
                                <th style={styles.tableHeaderCell}>
                                    <div style={styles.sortableHeader} onClick={() => handleSort('niin')}>
                                        NIIN
                                        {sortField === 'niin' && (
                                            <span style={styles.sortIcon}>
                                                {sortDirection === 'asc' ? '↑' : '↓'}
                                            </span>
                                        )}
                                    </div>
                                </th>
                                <th style={styles.tableHeaderCell}>
                                    <div style={styles.sortableHeader} onClick={() => handleSort('part_no')}>
                                        Part No
                                        {sortField === 'part_no' && (
                                            <span style={styles.sortIcon}>
                                                {sortDirection === 'asc' ? '↑' : '↓'}
                                            </span>
                                        )}
                                    </div>
                                </th>
                                <th style={styles.tableHeaderCell}>
                                    <div style={styles.sortableHeader}>
                                        Part Type
                                    </div>
                                </th>
                                <th style={styles.tableHeaderCell}>
                                    <div style={styles.sortableHeader} onClick={() => handleSort('nomen')}>
                                        Nomenclature
                                        {sortField === 'nomen' && (
                                            <span style={styles.sortIcon}>
                                                {sortDirection === 'asc' ? '↑' : '↓'}
                                            </span>
                                        )}
                                    </div>
                                </th>
                                <th style={styles.tableHeaderCell}>
                                    <div style={styles.sortableHeader} onClick={() => handleSort('qty_ord')}>
                                        Qty Ord
                                        {sortField === 'qty_ord' && (
                                            <span style={styles.sortIcon}>
                                                {sortDirection === 'asc' ? '↑' : '↓'}
                                            </span>
                                        )}
                                    </div>
                                </th>
                                <th style={styles.tableHeaderCell}>
                                    <div style={styles.sortableHeader} onClick={() => handleSort('ord_dt')}>
                                        Ord DT
                                        {sortField === 'ord_dt' && (
                                            <span style={styles.sortIcon}>
                                                {sortDirection === 'asc' ? '↑' : '↓'}
                                            </span>
                                        )}
                                    </div>
                                </th>
                                <th style={styles.tableHeaderCell}>
                                    <div style={styles.sortableHeader} onClick={() => handleSort('rcvd_dt')}>
                                        Rcvd DT
                                        {sortField === 'rcvd_dt' && (
                                            <span style={styles.sortIcon}>
                                                {sortDirection === 'asc' ? '↑' : '↓'}
                                            </span>
                                        )}
                                    </div>
                                </th>
                                <th style={styles.tableHeaderCell}>
                                    <div style={styles.sortableHeader} onClick={() => handleSort('est_delv_dt')}>
                                        Est Delv DT
                                        {sortField === 'est_delv_dt' && (
                                            <span style={styles.sortIcon}>
                                                {sortDirection === 'asc' ? '↑' : '↓'}
                                            </span>
                                        )}
                                    </div>
                                </th>
                                <th style={styles.tableHeaderCell}>
                                    <div style={styles.sortableHeader} onClick={() => handleSort('ui')}>
                                        UI
                                        {sortField === 'ui' && (
                                            <span style={styles.sortIcon}>
                                                {sortDirection === 'asc' ? '↑' : '↓'}
                                            </span>
                                        )}
                                    </div>
                                </th>
                                <th style={styles.tableHeaderCell}>
                                    <div style={styles.sortableHeader} onClick={() => handleSort('sup_sts')}>
                                        Sup STS
                                        {sortField === 'sup_sts' && (
                                            <span style={styles.sortIcon}>
                                                {sortDirection === 'asc' ? '↑' : '↓'}
                                            </span>
                                        )}
                                    </div>
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            {displayData.length === 0 ? (
                                <tr>
                                    <td colSpan="27" style={styles.noData}>
                                        No NMCS PMCS data available. Upload data to get started.
                                    </td>
                                </tr>
                            ) : (
                                displayData.map((row, index) => (
                                    <tr key={`${row.site_name}-${row.modex}-${row.jcn}-${index}`} style={styles.tableRow}>
                                        <td style={styles.tableCell}>
                                            <span style={styles.siteBadge}>{row.site_name}</span>
                                        </td>
                                        <td style={styles.tableCell}>{row.modex}</td>
                                        <td style={styles.tableCell}>{row.serno}</td>
                                        <td style={styles.tableCell}>{row.jcn}</td>
                                        <td style={styles.tableCell}>{row.mcn}</td>
                                        <td style={styles.tableCell}>{row.eoc_rcvd}</td>
                                        <td style={styles.tableCell}>{row.ti_mcn}</td>
                                        <td style={styles.tableCell}>{row.wc_cd}</td>
                                        <td style={styles.tableCell}>{row.wuc}</td>
                                        <td style={styles.tableCell}>{row.rqn_dt}</td>
                                        <td style={styles.tableCell}>{row.compute_0004}</td>
                                        <td style={styles.tableCell}>{row.rqn_no}</td>
                                        <td style={styles.tableCell}>{row.proj}</td>
                                        <td style={styles.tableCell}>{row.pri}</td>
                                        <td style={styles.tableCell}>{row.cog_cd}</td>
                                        <td style={styles.tableCell}>{row.cage}</td>
                                        <td style={styles.tableCell}>{row.fsc}</td>
                                        <td style={styles.tableCell}>{row.niin}</td>
                                        <td style={styles.tableCell}>{row.part_no}</td>
                                        <td style={styles.tableCell}>
                                            {(() => {
                                                const pt = lookupPartType(row.part_no);
                                                const badgeStyle = {
                                                    ...styles.partTypeBadge,
                                                    ...(pt === 'Consumable'
                                                        ? styles.partTypeConsumable
                                                        : pt === 'Repairable'
                                                            ? styles.partTypeRepairable
                                                            : styles.partTypeUnknown)
                                                };
                                                return <span style={badgeStyle}>{pt || 'Unknown'}</span>;
                                            })()}
                                        </td>
                                        <td style={styles.tableCell}>{row.nomen}</td>
                                        <td style={styles.tableCell}>{row.qty_ord}</td>
                                        <td style={styles.tableCell}>{row.ord_dt}</td>
                                        <td style={styles.tableCell}>{row.rcvd_dt}</td>
                                        <td style={styles.tableCell}>{row.est_delv_dt}</td>
                                        <td style={styles.tableCell}>{row.ui}</td>
                                        <td style={styles.tableCell}>{row.sup_sts}</td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            )}
            
            {/* Pagination Controls */}
            {!loading && totalPages > 1 && (
                <div style={styles.paginationContainer}>
                    <div style={styles.paginationInfo}>
                        Showing {((currentPage - 1) * pageSize) + 1} to {Math.min(currentPage * pageSize, totalCount)} of {totalCount} records
                    </div>
                    <div style={styles.paginationControls}>
                        <button
                            onClick={handlePreviousPage}
                            disabled={currentPage === 1}
                            style={currentPage === 1 ? styles.paginationButtonDisabled : styles.paginationButton}
                        >
                            Previous
                        </button>
                        
                        <div style={styles.pageNumbers}>
                            {getPageNumbers().map(pageNum => (
                                <button
                                    key={pageNum}
                                    onClick={() => handlePageChange(pageNum)}
                                    style={pageNum === currentPage ? styles.currentPage : styles.pageNumber}
                                >
                                    {pageNum}
                                </button>
                            ))}
                        </div>
                        
                        <button
                            onClick={handleNextPage}
                            disabled={currentPage === totalPages}
                            style={currentPage === totalPages ? styles.paginationButtonDisabled : styles.paginationButton}
                        >
                            Next
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
}

export default NMCSPMCSTable;